import React, { useMemo, useState } from 'react';
import { LoadingIndicator, Tooltip } from '@ygtechnologies/bygr-frontend-component-library';
import { useAsyncState, useAsyncStateWithIsLoading, useDefaultScrollToId } from '@/utils/hooks';
import { IdNamePair, ProjectCatalogProduct } from '@/types';
import { IState } from '@/state/IState';
import { useSelector } from 'react-redux';
import {
    searchProjectCatalogProducts,
    deleteProjectCatalogProduct,
    getProjectCatalogFilters,
} from '@/services/project-catalog';
import ProjectCatalogProductEditDialog from './ProjectCatalogProductEditDialog';
import { Trans } from '@lingui/react/macro';
import { ProjectCatalogImportDialog } from './ProjectCatalogImportDialog/ProjectCatalogImportDialog';
import { faExternalLink, faFileImport } from '@fortawesome/pro-light-svg-icons';
import { CatalogFilter, CatalogFilterValue } from '../catalog/CatalogFilter';
import { Button, ButtonBar, NoItemsCard, Badge } from '@ygtechnologies/rubus-ui';
import { ProjectProductCard } from '../products/ProductCard/ProjectProductCard';
import ScrollToWrapper from '@/components/ScrollToWrapper';
import { ProductCardList } from '../products/ProductCard/ProductCardList';
import theme from './ProjectCatalogProducts.module.scss';
import { ProjectRelativeLink } from '@/components/navigation';

type ProjectCatalogProductsProps = {};

const ProjectCatalogProducts: React.FC<ProjectCatalogProductsProps> = () => {
    const project = useSelector((state: IState) => state.global.activeProject!);
    const projectId = project.id;
    const [editingProductId, setEditingProductId] = useState<number | null>(null);
    const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
    const [showImportDialog, setShowImportDialog] = useState<boolean>(false);
    const [filter, setFilter] = useState<CatalogFilterValue>(CatalogFilterValue.empty());

    const [products, , reloadProducts, isLoading] = useAsyncStateWithIsLoading(
        () =>
            searchProjectCatalogProducts({
                projectId,
                itemCategoryIds: filter.itemCategoryIds,
                searchText: filter.searchText,
                suppliers: filter.suppliers.length === 0 ? null : filter.suppliers,
            }),
        [projectId, filter],
        null
    );

    const [filterData, , reloadFilterData] = useAsyncState(() => getProjectCatalogFilters(projectId), [projectId], null);

    const defaultSelectedId = useDefaultScrollToId();

    const handleEditProductClick = (product: ProjectCatalogProduct) => {
        setEditingProductId(product.id);
        setShowEditDialog(true);
    };

    const handleEditProductDialogClose = () => {
        setEditingProductId(null);
        setShowEditDialog(false);
    };

    const handleEditProductDialogSave = async () => {
        reloadProducts();
        reloadFilterData();
    };

    const handleDeleteProductClick = async (product: ProjectCatalogProduct) => {
        await deleteProjectCatalogProduct(product.id);
        reloadProducts();
        reloadFilterData();
    };

    if (!products || !filterData) {
        return <LoadingIndicator />;
    }

    return (
        <>
            <ButtonBar sticky margins="none">
                <Button icon={faFileImport} variant="tertiary" onClick={() => setShowImportDialog(true)}>
                    <Trans>Import from catalog</Trans>
                </Button>

                <ButtonBar.Right>
                    <CatalogFilter isLoading={isLoading} filter={filter} onChange={setFilter} data={filterData} />
                </ButtonBar.Right>
            </ButtonBar>

            {products.length === 0 ? (
                <NoItemsCard
                    title={<Trans>There are no products!</Trans>}
                    description={<Trans>Want to import products from catalog?</Trans>}
                    buttonProps={{
                        icon: faFileImport,
                        onClick: () => setShowImportDialog(true),
                        children: <Trans>Import from catalog</Trans>,
                    }}
                />
            ) : (
                <ProductCardList>
                    {products.map((p) => (
                        <ScrollToWrapper key={p.id} shouldScroll={p.id === defaultSelectedId}>
                            <ProjectProductCard
                                product={p}
                                onEditClick={() => handleEditProductClick(p)}
                                onDeleteClick={() => handleDeleteProductClick(p)}
                                bottomElement={<BottomElement productLinkGroupReferences={p.linkGroupReferences ?? []} />}
                            />
                        </ScrollToWrapper>
                    ))}
                </ProductCardList>
            )}

            <ProjectCatalogProductEditDialog
                show={showEditDialog}
                projectCatalogProductId={editingProductId}
                onClose={handleEditProductDialogClose}
                onSave={handleEditProductDialogSave}
            />

            <ProjectCatalogImportDialog
                show={showImportDialog}
                onClose={() => {
                    setShowImportDialog(false);
                    reloadProducts();
                }}
            />
        </>
    );
};

export default ProjectCatalogProducts;

const BottomElement: React.FC<{ productLinkGroupReferences: IdNamePair[] }> = ({ productLinkGroupReferences }) => {
    if (productLinkGroupReferences.length === 0) return null;

    return (
        <div className={theme.bottomElement}>
            {(productLinkGroupReferences ?? []).map((x) => (
                <Tooltip
                    interactive
                    key={x.id}
                    content={
                        <div className={theme.groupTooltip}>
                            <span className={theme.groupTooltipName}>{x.name}</span>
                            <Button
                                as={ProjectRelativeLink}
                                to={`/options/options?groupId=${x.id}`}
                                size="sm"
                                variant="link"
                                icon={faExternalLink}>
                                <Trans>Go to group</Trans>
                            </Button>
                        </div>
                    }>
                    <Badge maxWidth={288}>{x.name}</Badge>
                </Tooltip>
            ))}
        </div>
    );
};
